import React from "react";
import {
  DataGrid,
  ptBR,
  GridToolbarContainer,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid";
import "./table.css";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import GetAppIcon from "@mui/icons-material/GetApp";
import { withStyles } from "@material-ui/core";

const StyledDataGrid = withStyles({
  root: {
    "& .MuiDataGrid-renderingZone": {
      maxHeight: "none !important",
    },
    "& .MuiDataGrid-panelWrapper": {
      width: "100px",
    },
    "& .MuiDataGrid-cell": {
      lineHeight: "unset !important",
      maxHeight: "none !important",
      whiteSpace: "normal",
      fontSize: "calc(6px + 0.4vw)",
    },
    "& .MuiDataGrid-row": {
      maxHeight: "none !important",
    },
    "& .MuiDataGrid-toolbarContainer": {
      minHeight: "30px",
      maxHeight: "30px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-root": {
      marginLeft: "-2vh",
    },
  },
})(DataGrid);

// ----------------------
// Botão customizado com loading
// ----------------------
const CustomExportButton = () => {
  const apiRef = useGridApiContext();
  const [loading, setLoading] = React.useState(false);

  const handleExport = () => {
    setLoading(true);

    // Garante que o loading apareça antes de iniciar a exportação
    requestAnimationFrame(() => {
      apiRef.current.exportDataAsCsv({
        delimiter: ";",
      });

      // exportDataAsCsv é síncrono: quando termina, o download já foi disparado
      setLoading(false);
    });
  };

  return (
    <Button
      size="small"
      onClick={handleExport}
      startIcon={!loading && <GetAppIcon />}
      disabled={loading}
      variant="text"
    >
      {loading ? <CircularProgress size={18} color="inherit" /> : "Exportar"}
    </Button>
  );
};

const CustomToolbar = ({ setFilterButtonEl }) => (
  <GridToolbarContainer>
    <GridToolbarFilterButton ref={setFilterButtonEl} />
    <CustomExportButton />
  </GridToolbarContainer>
);

function msToTime(ms) {
  if (!ms) return "";

  const totalMilliseconds = Number(ms);
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  const milliseconds = (totalMilliseconds % 1000).toString().padStart(3, "0");

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

function formatDateWithTimezone(value) {
  if (!value) return "";

  const date = new Date(value);

  const formatter = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour12: false,
    timeZone: "America/Sao_Paulo",
  });

  const parts = formatter.formatToParts(date);

  const get = (type) => {
    const part = parts.find((p) => p.type === type);
    return part ? part.value : "";
  };

  const time = `${get("hour")}:${get("minute")}:${get("second")}`;
  const milliseconds = date.getMilliseconds().toString().padStart(3, "0");
  const fullDate = `${get("day")}/${get("month")}/${get("year")}`;

  return `${time}.${milliseconds} - ${fullDate}`;
}

// Campos de duração em milissegundos
const durationFields = [
  "duration",
  "genesysDuration",
  "espressoDuration",
  "brokerDuration",
];
const timezonesFields = ["DataHora"];

const baseColumns = [
  { field: "Data", headerName: "Data", flex: 1 },
  { field: "Interações Inbound", headerName: "Interações Inbound", flex: 1 },
  { field: "Interações Outbound", headerName: "Interações Outbound", flex: 1 },
  { field: "Interações", headerName: "Interações", flex: 1 },
];

const columns = baseColumns.map((col) => ({
  ...col,
  // renderHeader: (params) => {
  //   const tip = headerTooltipsByField[col.field];
  //   const label = <span>{params.colDef.headerName}</span>;
  //   return tip ? <Tooltip title={tip}>{label}</Tooltip> : label;
  // },
  renderCell: (params) => {
    const isDuration = durationFields.includes(col.field);
    const isTimezone = timezonesFields.includes(col.field);

    let displayValue = params.value;
    if (isDuration) displayValue = msToTime(params.value);
    else if (isTimezone) displayValue = formatDateWithTimezone(params.value);

    const content = (
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
      >
        {displayValue ?? ""}
      </span>
    );

    return <Tooltip title={displayValue ?? ""}>{content}</Tooltip>;
  },
}));

export default function InteractionsByHour(data) {
  const processRows = (rows) =>
    rows.map((row, index) => ({
      ...row,
      id: `${row.origin}-${row.phone}-${index}`,
    }));
  const rows = processRows(data.data);

  return (
    <div style={{ height: "110%", width: "100%" }}>
      <StyledDataGrid
        disableSelectionOnClick
        components={{
          Toolbar: CustomToolbar,
        }}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        getRowId={(row) => row.id}
        rows={rows}
        // @ts-ignore
        columns={columns}
      />
    </div>
  );
}
